import React, { useRef, useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { toggleWishlist, getActiveCampaigns } from '../services/api';
import useAuthStore from '../context/authStore';
import toast from 'react-hot-toast';
import './CourseCard.css';

export default function CourseCard({ course }) {
  const cardRef = useRef(null);
  const navigate = useNavigate();
  const { user, userDoc, refreshUserDoc } = useAuthStore();
  const [wishloading, setWishloading] = useState(false);
  const [campaign, setCampaign] = useState(null);

  useEffect(() => {
    // Only fetch campaigns for non-enrolled students/public
    getActiveCampaigns().then(r => {
      const active = (r.data || []).find(c => {
        if (c.applicableCourses?.length > 0 && !c.applicableCourses.includes(course.id)) return false;
        if (c.applicableCategories?.length > 0 && !c.applicableCategories.includes(course.subject)) return false;
        return true;
      });
      setCampaign(active || null);
    }).catch(() => {});
  }, [course.id, course.subject]);

  const isWishlisted = userDoc?.wishlist?.includes(course.id);
  const stars = (r) => '★'.repeat(Math.round(r || 0)) + '☆'.repeat(5 - Math.round(r || 0));

  const handleWishlist = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) return toast.error('سجّل دخولك أولاً لحفظ الكورسات');
    setWishloading(true);
    try {
      await toggleWishlist(course.id);
      await refreshUserDoc();
      toast.success(isWishlisted ? 'تمت الإزالة من المفضلة' : 'تمت الإضافة للمفضلة ❤️');
    } catch {
      toast.error('حدث خطأ');
    }
    setWishloading(false);
  };

  /* ── 3-D tilt on mouse move ── */
  const handleMouseMove = (e) => {
    const card = cardRef.current;
    if (!card) return;
    const { left, top, width, height } = card.getBoundingClientRect();
    const x = (e.clientX - left) / width  - 0.5;  // -0.5 → +0.5
    const y = (e.clientY - top)  / height - 0.5;
    card.style.transform = `translateY(-6px) rotateY(${x * 12}deg) rotateX(${-y * 12}deg)`;
  };

  const handleMouseLeave = () => {
    const card = cardRef.current;
    if (!card) return;
    card.style.transform = '';
  };

  return (
    <Link
      to={`/courses/${course.slug || course.id}`}
      ref={cardRef}
      className="course-card card-shimmer card-tilt"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* Thumbnail */}
      <div className="course-thumb">
        {course.thumbnail
          ? <img src={course.thumbnail} alt={course.title} className="course-thumb-img" />
          : (
            <div className="course-thumb-placeholder">
              <span className="thumb-subject-letter">{course.subject?.[0] || '📚'}</span>
            </div>
          )
        }
        {course.isLive && <span className="live-badge badge-pulse">🔴 لايف</span>}
        {course.isFeatured && <span className="featured-badge">⭐ مميز</span>}
        {campaign && (
          <span className="campaign-badge-float">
            خصم {campaign.discountType === 'percentage' ? `${campaign.discountValue}%` : `${campaign.discountValue} ج.م`} 🔥
          </span>
        )}

        {/* Wishlist Button */}
        <button
          className={`wishlist-btn ${isWishlisted ? 'active heart-pulse-active' : ''}`}
          onClick={handleWishlist}
          disabled={wishloading}
          title={isWishlisted ? 'إزالة من المفضلة' : 'إضافة للمفضلة'}
          key={isWishlisted ? 'active' : 'inactive'}
        >
          {isWishlisted ? '❤️' : '🤍'}
        </button>

        <div className="thumb-overlay" />
      </div>

      {/* Body */}
      <div className="course-card-body">
        <span className="course-subject badge badge-blue">{course.subject}</span>
        <h3 className="course-title">{course.title}</h3>
        <div className="course-teacher-row" style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
          {course.teacherAvatar
            ? <img src={course.teacherAvatar} alt={course.teacherName} className="teacher-avatar-sm" style={{ width: 32, height: 32, borderRadius: '50%' }} />
            : <div className="teacher-dot" style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--blue-light)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 14 }}>{course.teacherName?.[0]}</div>}
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Link
              to={`/teachers/${course.teacherUsername || course.teacherId}`}
              onClick={(e) => e.stopPropagation()}
              style={{ fontSize: 13, fontWeight: 700, color: 'inherit', textDecoration: 'none' }}
            >
              {course.teacherName}
            </Link>
            {course.teacherTitle && <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>{course.teacherTitle}</span>}
          </div>
        </div>

        <div className="course-meta">
          <span className="stars">{stars(course.rating)}</span>
          <span className="course-reviews">({course.totalReviews || 0})</span>
          <span className="course-students">· {course.enrolledCount || 0} طالب</span>
        </div>

        <div className="course-footer">
          <div className="course-price">
            {course.price === 0
              ? <span className="price-free">مجاني 🎁</span>
              : campaign
                ? <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <span style={{ textDecoration: 'line-through', fontSize: '11px', color: 'var(--text-muted)', marginBottom: '-4px' }}>{course.price} ج.م</span>
                    <span className="price-paid">
                      {campaign.discountType === 'percentage'
                        ? Math.round(course.price * (1 - campaign.discountValue / 100))
                        : Math.max(0, course.price - campaign.discountValue)} ج.م
                    </span>
                  </div>
                : <span className="price-paid">{course.price} ج.م</span>
            }
          </div>
          <span className="course-lessons">{course.lessons?.length || 0} درس</span>
        </div>

        <div className="course-card-actions" style={{ marginTop: 15 }}>
          <button
            className="btn btn-primary btn-sm"
            style={{ width: '100%', justifyContent: 'center' }}
            onClick={(e) => {
              e.preventDefault();
              navigate(`/courses/${course.slug || course.id}`);
            }}
          >
            🛒 اشترِ الآن
          </button>
        </div>
      </div>

      {/* Hover arrow indicator */}
      <div className="card-arrow">←</div>
    </Link>
  );
}
